import { useMemo } from "react";
// Default node dimensions (overridable via options). Matches process-map's
// NODE_WIDTH/NODE_HEIGHT to keep behavior unchanged when options omitted.
const DEFAULT_NODE_WIDTH = 260;
const DEFAULT_NODE_HEIGHT = 72;
export function computeSpreading(edges, nodeMap, options) {
    const { spacing, maxSpread, tolerance } = options;
    const result = new Map();
    const edgeMids = [];
    for (const edge of edges){
        const srcNode = nodeMap.get(edge.source);
        const tgtNode = nodeMap.get(edge.target);
        if (!srcNode || !tgtNode) continue;
        const sourceX = srcNode.x + srcNode.width;
        const sourceY = srcNode.y + srcNode.height / 2;
        const targetX = tgtNode.x;
        const midX = Math.round((sourceX + targetX) / 2);
        edgeMids.push({
            id: edge.id,
            midX,
            sourceY
        });
    }
    edgeMids.sort((a, b)=>a.midX - b.midX);
    const corridors = [];
    let currentGroup = [];
    for (const info of edgeMids){
        if (currentGroup.length === 0 || Math.abs(info.midX - currentGroup[0].midX) <= tolerance) {
            currentGroup.push(info);
        } else {
            if (currentGroup.length > 1) corridors.push(currentGroup);
            currentGroup = [
                info
            ];
        }
    }
    if (currentGroup.length > 1) corridors.push(currentGroup);
    for (const group of corridors){
        const n = group.length;
        const totalWidth = (n - 1) * spacing;
        const effectiveSpacing = totalWidth > maxSpread ? maxSpread / (n - 1) : spacing;
        const effectiveTotal = (n - 1) * effectiveSpacing;
        const baseMidX = group.reduce((sum, e)=>sum + e.midX, 0) / n;
        group.sort((a, b)=>a.sourceY - b.sourceY);
        for(let i = 0; i < n; i++){
            const offset = -effectiveTotal / 2 + i * effectiveSpacing;
            result.set(group[i].id, baseMidX + offset);
        }
    }
    return result;
}
export function useEdgeSpreading(edges, nodes, options) {
    const spacing = options?.spacing ?? 12;
    const maxSpread = options?.maxSpread ?? 60;
    const tolerance = options?.tolerance ?? 2;
    const nodeWidth = options?.nodeWidth ?? DEFAULT_NODE_WIDTH;
    const nodeHeight = options?.nodeHeight ?? DEFAULT_NODE_HEIGHT;
    const nodeMap = useMemo(()=>{
        const map = new Map();
        for (const node of nodes){
            map.set(node.id, {
                x: node.position.x,
                y: node.position.y,
                width: node.measured?.width ?? node.width ?? nodeWidth,
                height: node.measured?.height ?? node.height ?? nodeHeight
            });
        }
        return map;
    }, [
        nodes,
        nodeWidth,
        nodeHeight
    ]);
    return useMemo(()=>computeSpreading(edges, nodeMap, {
            spacing,
            maxSpread,
            tolerance
        }), [
        edges,
        nodeMap,
        spacing,
        maxSpread,
        tolerance
    ]);
}

//# sourceMappingURL=useEdgeSpreading.js.map